import React, { FC } from "react";
import styled from "@emotion/styled";
import Icon from "src/icons";
import Modal from "src/shared/components/Modal";
import colors from "src/styles/variables.scss";
import { ReactComponent as LockIconSvg } from "src/assets/lock-icon.svg";
import { ReactComponent as CloseIconSvg } from "src/assets/close-icon.svg";
import { UserRoles } from "src/store/app";
import { IReduxOperations } from "src/store";
import { animated, useSpring } from "react-spring";

const Holder = styled(animated.section)`
  display: grid;
  background: ${colors.bluishGray};
  height: 100%;
  width: inherit;
  z-index: 10;
  position: fixed;
  color: white;
  opacity: 0;
  grid-template-rows: minmax(min-content, 100px) auto;
`;

const Head = styled.header`
  display: grid;
  grid-template-columns: min-content minmax(min-content, 100%) min-content;
  grid-column-gap: 10px;
  justify-content: space-between;
  align-items: center;
  background: ${colors.l2Background};
  padding: 0 20px;
  justify-items: start;
`;

const LockIcon = styled(Icon)`
  font-size: 20px;
`;
const HeadingText = styled.h3`
  margin: 0;
  color: white;
  font-weight: bold;
  justify-self: start;
`;
const CloseIcon = styled(Icon)`
  justify-self: end;
  font-size: 24px;
`;

const Body = styled.main`
  padding: 24px 20px;
  width: inherit;
`;

const AccessItem = styled.section`
  border-bottom: 1px solid #434349;
  margin-bottom: 21px;
  &:last-of-type {
    margin-bottom: 32px;
  }
`;
const AccessItemTitle = styled.h5`
  text-transform: uppercase;
  letter-spacing: 1px;
  color: inherit;
  margin-bottom: 0;
`;

const AccessItemList = styled.div`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 19px 0;
  margin: 0;
  color: ${colors.mutedGray};
`;

const FooterInfo = styled.footer`
  border: 1px dashed #5e5a5a;
  padding: 12px;
`;
const FooterText = styled.p`
  color: ${colors.mutedGray1};
  margin: 0;
  > a {
    color: ${colors.themeBlue};
  }
`;

type Props = {
  roles: IReduxOperations<UserRoles>;
  onClose?: () => void;
};

const Permissions: FC<Props> = ({ onClose, roles: rolesOperation }) => {
  const { hasFullAccess, metrics, cities, podNames } = rolesOperation.payload;
  const styles = useSpring({
    from: { translateY: "100%", opactity: 0 },
    to: { translateY: "0", opacity: 1 },
    config: { duration: 300 },
  });

  return (
    <Modal>
      <Holder style={styles}>
        <Head>
          <LockIcon component={LockIconSvg} />
          <HeadingText>Permissions</HeadingText>
          <CloseIcon component={CloseIconSvg} onClick={onClose} />
        </Head>
        <Body>
          {rolesOperation.success ? (
            <>
              <AccessItem>
                <AccessItemTitle>Permitted Cities</AccessItemTitle>
                <AccessItemList>
                  {hasFullAccess
                    ? "All"
                    : cities.slice(0, 5).join(", ") + " " + (cities.length > 5 ? `+${cities.length - 5} more` : "")}
                </AccessItemList>
              </AccessItem>
              <AccessItem>
                <AccessItemTitle>Permitted Metrics</AccessItemTitle>
                <AccessItemList>
                  {hasFullAccess
                    ? "All"
                    : metrics.slice(0, 5).join(", ") + " " + (metrics.length > 5 ? `+${metrics.length - 5} more` : "")}
                </AccessItemList>
              </AccessItem>
              <AccessItem>
                <AccessItemTitle>Permitted Pods</AccessItemTitle>
                <AccessItemList>
                  {hasFullAccess
                    ? "All"
                    : podNames.slice(0, 5).join(", ") + " " + (metrics.length > 5 ? `+${metrics.length - 5} more` : "")}
                </AccessItemList>
              </AccessItem>
              <FooterInfo>
                <FooterText>
                  Need access to more data? Drop a request at{" "}
                  <a href="https://app.slack.com/client/E05KBNC81JR/C03GEC2RDSR"> #anobis-support </a>
                </FooterText>
              </FooterInfo>
            </>
          ) : rolesOperation.failure ? (
            "Something went wrong"
          ) : null}
        </Body>
      </Holder>
    </Modal>
  );
};

export default Permissions;

